import React, { useState } from "react";
// Components
import { ChatMessage, ChatMyMessage } from "@fluentui-contrib/react-chat";
import { Avatar, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Popover, PopoverSurface, PopoverTrigger, Toolbar, ToolbarButton, mergeClasses } from "@fluentui/react-components";
import { Delete20Filled, Delete20Regular, Edit20Filled, Edit20Regular, bundleIcon, MoreHorizontal24Filled, MentionRegular } from "@fluentui/react-icons";
import { UserContainer } from "../user/UserContainer";
// Constants
import { Key } from "@ms/uno-constants/lib/local/KeyCode";
// Styles
import { useClasses } from "./ChatMessageItem.styles";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { formatChatTimestamp, processMessageHtmlContent, shouldShowHeader } from "./ChatMessageItemUtilities";
import { createTrustedHtml } from "@ms/uno-utilities/lib/local/TrustedTypeUtilities";
const EditIcon = bundleIcon(Edit20Filled, Edit20Regular);
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);
var ChatMessageItemFocusState;
/** Enum representing the focus state of the chat message item */ (function(ChatMessageItemFocusState) {
    /** Chat message is not focused */ ChatMessageItemFocusState[ChatMessageItemFocusState["NotFocused"] = 0] = "NotFocused";
    /** Chat message body is focused */ ChatMessageItemFocusState[ChatMessageItemFocusState["FocusedOnMessage"] = 1] = "FocusedOnMessage";
    /** Toolbar is focused */ ChatMessageItemFocusState[ChatMessageItemFocusState["FocusedOnToolbar"] = 2] = "FocusedOnToolbar";
})(ChatMessageItemFocusState || (ChatMessageItemFocusState = {}));
export const ChatMessageItem = ({ message, onDeleteMessage, onEditMessage, currentUserId, getUserPhoto, getUserById, previousMessage, showToolbar = true, onRenderPersona, toolbarMountNode })=>{
    const messageBodyElementRef = React.useRef(null);
    const toolbarRef = React.useRef(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [focusState, setFocusState] = useState(0);
    const classNames = useClasses();
    const authorUserId = message.createdBy.user.id;
    const isCurrentUser = authorUserId === currentUserId;
    const authorUser = getUserById(authorUserId).data;
    const avatarName = authorUser?.formattedDisplayName ?? Strings.TaskComments_UnknownUserLabel;
    const showHeader = shouldShowHeader(message, previousMessage);
    const isCurrentUserMentioned = message.properties.mentions?.some((mention)=>mention.mentioned === currentUserId);
    const authorElementId = `author-${message.id}`;
    const timestampElementId = `timestamp-${message.id}`;
    const contentElementId = `content-${message.id}`;
    // Track number of mention users being fetched so we can trigger message re-processing when fetching finishes
    const mentionedUsersBeingFetchedCount = (message.properties.mentions ?? []).filter((mention)=>{
        const userData = getUserById(mention.mentioned);
        return userData.isFetching;
    }).length;
    // Memoize processed HTML content to avoid re-processing on every render
    const messageHtmlContentToRender = React.useMemo(()=>{
        const html = processMessageHtmlContent(message, currentUserId, getUserById);
        // Convert processed HTML string to TrustedHTML for CSP compliance with dangerouslySetInnerHTML
        return createTrustedHtml(html, true); // html is sanitized in processMessageHtmlContent
    // Disable react-hooks/exhaustive-deps because mentionedUsersBeingFetchedCount is not used, but we need it to trigger re-processing when user data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        message,
        currentUserId,
        getUserById,
        mentionedUsersBeingFetchedCount
    ]);
    const messageContent = message.isDeleted ? /*#__PURE__*/ React.createElement("div", {
        id: contentElementId,
        className: classNames.deletedMessageContent
    }, Strings.TaskChat_MessageDeleted) : /*#__PURE__*/ React.createElement("div", {
        id: contentElementId,
        className: classNames.chatMessageContent,
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML: {
            __html: messageHtmlContentToRender
        }
    });
    const handleEdit = ()=>{
        onEditMessage?.(message.id);
        setIsPopoverOpen(false);
        setIsMenuOpen(false);
    };
    const handleDelete = ()=>{
        onDeleteMessage?.(message.id);
        setIsPopoverOpen(false);
        setIsMenuOpen(false);
    };
    // Update the popover state based on hover, menu, and focus state
    React.useEffect(()=>{
        setIsPopoverOpen(isHovering || isMenuOpen || focusState !== 0);
    }, [
        isHovering,
        isMenuOpen,
        focusState
    ]);
    const handleMouseEnter = ()=>setIsHovering(true);
    const handleMouseLeave = ()=>setIsHovering(false);
    const handleFocus = ()=>setFocusState(1);
    const handleBlur = ()=>{
        // If no toolbar or toolbar is not focused, clear focus state
        const toolbarElement = toolbarRef.current;
        if (!toolbarElement?.contains(document.activeElement)) {
            setFocusState(0);
        }
    };
    const handleKeyDown = (event)=>{
        if (event.target === messageBodyElementRef.current) {
            // Pressing Enter when focused on message body moves focus to first available toolbar button
            if (event.key === Key.ENTER) {
                event.preventDefault();
                const toolbar = toolbarRef.current;
                if (toolbar) {
                    const firstButton = toolbar.querySelector("button");
                    if (firstButton) {
                        firstButton.focus();
                        setFocusState(2);
                    }
                }
            }
        }
    };
    const handleToolbarKeyDown = (event)=>{
        // Pressing Esc returns focus to the message body element
        if (event.key === Key.ESCAPE) {
            event.preventDefault();
            event.stopPropagation();
            const messageBodyElement = messageBodyElementRef.current;
            if (messageBodyElement) {
                messageBodyElement.focus();
            }
        }
    };
    const renderNameline = ()=>{
        const namelineClass = isCurrentUser ? classNames.namelineContainerCurrentUser : classNames.namelineContainer;
        const formattedTimestamp = formatChatTimestamp(message.createdDateTime);
        return /*#__PURE__*/ React.createElement("div", {
            className: mergeClasses(namelineClass, !showHeader && classNames.namelineHidden)
        }, /*#__PURE__*/ React.createElement("span", {
            id: authorElementId,
            className: mergeClasses(isCurrentUser && classNames.namelineHidden, classNames.name),
            title: avatarName
        }, avatarName), /*#__PURE__*/ React.createElement("span", {
            id: timestampElementId
        }, " ", formattedTimestamp));
    };
    const renderAvatar = ()=>{
        if (!showHeader || isCurrentUser) {
            return undefined;
        }
        const imageSrc = getUserPhoto(authorUserId) ?? undefined;
        return /*#__PURE__*/ React.createElement(UserContainer, {
            user: authorUser,
            onLoad: onRenderPersona,
            containerId: `userContainer-chatMessageItemAvatar-${message.id}`
        }, /*#__PURE__*/ React.createElement(Avatar, {
            name: avatarName,
            image: {
                src: imageSrc
            }
        }));
    };
    const chatMessageProps = {
        key: message.id,
        avatar: renderAvatar(),
        author: undefined,
        body: {
            className: isCurrentUser ? classNames.chatMyMessage : classNames.chatMessage,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onFocus: handleFocus,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            ref: messageBodyElementRef,
            "aria-labelledby": [
                authorElementId,
                contentElementId,
                timestampElementId
            ].join(" ")
        },
        timestamp: undefined,
        root: {
            className: classNames.chatMessageRoot
        },
        decorationIcon: isCurrentUserMentioned ? /*#__PURE__*/ React.createElement(MentionRegular, {
            "aria-label": StringHelper.format(Strings.TaskChat_MentionedYouAriaLabel, avatarName)
        }) : undefined
    };
    const ChatMessageComponent = isCurrentUser ? ChatMyMessage : ChatMessage;
    // No toolbar or deleted message -> simple render
    if (!showToolbar || !onEditMessage && !onDeleteMessage || message.isDeleted) {
        return /*#__PURE__*/ React.createElement("div", {
            className: mergeClasses(classNames.messageContainer, showHeader && classNames.messageContainerWithHeader)
        }, renderNameline(), /*#__PURE__*/ React.createElement("div", {
            className: classNames.messageWrapper
        }, /*#__PURE__*/ React.createElement(ChatMessageComponent, chatMessageProps, messageContent)));
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: mergeClasses(classNames.messageContainer, showHeader && classNames.messageContainerWithHeader)
    }, renderNameline(), /*#__PURE__*/ React.createElement("div", {
        className: classNames.messageWrapper
    }, /*#__PURE__*/ React.createElement(Popover, {
        open: isCurrentUser && isPopoverOpen,
        onOpenChange: (e, data)=>{
            // Only allow closing if menu is not open
            if (!data.open && !isMenuOpen) {
                setIsPopoverOpen(false);
            }
        },
        openOnHover: true,
        positioning: {
            position: "above",
            align: "end",
            pinned: true
        },
        mountNode: toolbarMountNode,
        unstable_disableAutoFocus: true
    }, /*#__PURE__*/ React.createElement(PopoverTrigger, {
        disableButtonEnhancement: true
    }, /*#__PURE__*/ React.createElement(ChatMessageComponent, chatMessageProps, messageContent)), /*#__PURE__*/ React.createElement(PopoverSurface, {
        className: classNames.popoverSurface,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave
    }, /*#__PURE__*/ React.createElement(Toolbar, {
        "aria-label": Strings.Tooltip_MoreButtonTooltipText,
        size: "small",
        onKeyDown: handleToolbarKeyDown,
        ref: toolbarRef
    }, onEditMessage && /*#__PURE__*/ React.createElement(ToolbarButton, {
        appearance: "transparent",
        icon: /*#__PURE__*/ React.createElement(EditIcon, null),
        onClick: handleEdit,
        "aria-label": Strings.TaskEditorDialog_EditMessageAriaLabel
    }), /*#__PURE__*/ React.createElement(Menu, {
        open: isMenuOpen,
        onOpenChange: (e, data)=>setIsMenuOpen(data.open)
    }, /*#__PURE__*/ React.createElement(MenuTrigger, null, /*#__PURE__*/ React.createElement(ToolbarButton, {
        "aria-label": Strings.Common_MoreOptions,
        icon: /*#__PURE__*/ React.createElement(MoreHorizontal24Filled, null),
        appearance: "transparent"
    })), /*#__PURE__*/ React.createElement(MenuPopover, null, /*#__PURE__*/ React.createElement(MenuList, null, /*#__PURE__*/ React.createElement(MenuItem, {
        icon: /*#__PURE__*/ React.createElement(DeleteIcon, null),
        onClick: handleDelete
    }, Strings.TaskEditorDialog_DeleteTaskText)))))))));
};
